/**
 * Store + TenantSettings Zod schemalar
 *
 * Kanonik:
 *   - Store 1:1 TenantSettings (har do'kon o'z brand sozlamalari bilan)
 *   - Hech qanday hardcoded brand (telefon, telegram, email) — TenantSettings'da
 *   - defaultMarkup default 25%
 *   - vipOrderCount default 10
 *   - lowStockThreshold default 5
 *   - baseCurrency default UZS
 */
import { z } from 'zod';
import { phoneSchema } from './common.schema.js';
import { currencyEnum } from './product.schema.js';
// ─── Tenant settings ──────────────────────────────────────────────
export const tenantSettingsSchema = z.object({
    // Brand
    shopName: z.string().min(1, "Do'kon nomi bo'sh bo'lmasin").max(100),
    supportPhone: z.string().max(20).optional(),
    supportEmail: z.string().email("Noto'g'ri email").max(100).optional().or(z.literal('')),
    supportAddress: z.string().max(255).optional(),
    shopLogo: z.string().url("Noto'g'ri logo URL").max(500).optional().or(z.literal('')),
    // Ijtimoiy tarmoqlar
    telegramChannel: z.string().max(100).optional(),
    instagramHandle: z.string().max(100).optional(),
    websiteUrl: z.string().url("Noto'g'ri website URL").max(200).optional().or(z.literal('')),
    // Telegram bot
    telegramBotUsername: z.string().max(100).optional(),
    telegramBotToken: z.string().max(255).optional(),
    telegramChatId: z.string().max(50).optional(),
    telegramEnabled: z.boolean().default(false),
    // Printer
    defaultPrinter: z.string().max(100).optional(),
    printAuto: z.boolean().default(false),
    receiptFooter: z.string().max(500).optional(),
    // Currency va kurslar
    baseCurrency: currencyEnum.default('UZS'),
    usdRate: z.number().min(0).max(1_000_000).default(12500),
    rubRate: z.number().min(0).max(1_000_000).default(140),
    cnyRate: z.number().min(0).max(1_000_000).default(1750),
    // Biznes qoidalar
    lowStockThreshold: z.number().int().min(0).max(10_000).default(5),
    vipOrderCount: z.number().int().min(1).max(10_000).default(10),
    defaultMarkup: z.number().min(0).max(1_000).default(25),
});
export const updateTenantSettingsSchema = tenantSettingsSchema.partial();
// ─── Create store ─────────────────────────────────────────────────
export const createStoreSchema = z
    .object({
    name: z.string().min(2, "Do'kon nomi kamida 2 ta belgi").max(100),
    address: z.string().max(255).optional(),
    phone: phoneSchema.optional(),
    isMain: z.boolean().default(false),
    // Ixtiyoriy: birga TenantSettings yaratish
    settings: tenantSettingsSchema.partial().optional(),
    // ─── Do'kon admini (ixtiyoriy) ───────────────────────────────
    // Berilsa — shu do'konga bog'langan ADMIN hisob yaratiladi.
    // ADMIN faqat o'z do'konini boshqaradi; EGASI barcha do'konni ko'radi.
    adminUsername: z.string().min(3, 'Login kamida 3 ta belgi').max(50).optional(),
    adminPassword: z.string().min(6, 'Parol kamida 6 ta belgi').max(100).optional(),
    adminName: z.string().max(100).optional(),
    // Asosiy do'kon (isMain) mahsulot katalogini klon qilish (stock=0 dan boshlanadi)
    cloneProducts: z.boolean().default(false),
})
    .refine((d) => (d.adminUsername === undefined) === (d.adminPassword === undefined), {
    message: 'Login va parol birga kiritilishi kerak',
    path: ['adminPassword'],
});
// ─── Update store ─────────────────────────────────────────────────
// Do'kon ma'lumoti + do'kon ADMIN hisobi (login/parol/ism). Admin maydonlari
// optional: adminUsername berilsa — mavjud admin login'i yangilanadi (yo'q bo'lsa
// yangi admin yaratiladi, adminPassword shart). adminPassword bo'sh = parol o'zgarmaydi.
export const updateStoreSchema = z.object({
    name: z.string().min(2).max(100).optional(),
    address: z.string().max(255).optional(),
    phone: phoneSchema.optional(),
    isMain: z.boolean().optional(),
    isActive: z.boolean().optional(),
    adminUsername: z.string().min(3, 'Login kamida 3 ta belgi').max(50).optional(),
    adminPassword: z.string().min(6, 'Parol kamida 6 ta belgi').max(100).optional(),
    adminName: z.string().max(100).optional(),
});
// ─── Store query ──────────────────────────────────────────────────
export const storeQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    search: z.string().max(100).optional(),
    isActive: z.coerce.boolean().optional(),
});
// ─── Marketplace'ga qo'shilishni rad etish (EGASI, sabab bilan) ──────
export const rejectStoreMarketplaceSchema = z.object({
    reason: z.string().trim().max(300).optional(),
});
//# sourceMappingURL=store.schema.js.map